/*
  Одна находка реестра банкротств: что нашли и два решения - вариант Б, 27.10.2026.

  🔴 Дата принятия заявления о банкротстве обязательна и вносится ЧЕЛОВЕКОМ: реестр
  отдельным полем её не отдаёт, а от неё считается граница реестрового и текущего долга
  (§6.10.2). Дату акта о введении процедуры, что пришла из реестра, сюда не подставляем:
  подставленная дата молча сдвинула бы деньги между двумя режимами взыскания.

  «Это не он» требует причины и отвечает быстро: остановка по однофамильцу - потерянный
  долг, а причина нужна, чтобы ту же карточку не предлагали снова.

  Проверки делает сервер; здесь - что видит человек и что он отправляет.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { Field, Notice } from './Common.jsx'
import Select from './Select.jsx'

const KINDS = { judicial: 'Судебное', extrajudicial: 'Внесудебное (через МФЦ)' }

/* Реестр называет вид процедуры не всегда: тогда его выбирает человек, а не система.
   Пустое поле не отправляется: сервер отличает «не указано» от значения. */
function payloadOf(finding, values) {
  const payload = {
    kind: finding.kind ?? values.kind,
    state: finding.registry_state === 'completed' ? 'completed' : 'introduced',
    opened_on: values.opened_on,
  }
  ;['case_number', 'claims_deadline', 'completed_on'].forEach((key) => {
    const value = String(values[key]).trim()
    if (value !== '') payload[key] = value
  })
  return payload
}

export default function RegistryFindingDecision({ finding, onDone }) {
  const [values, setValues] = useState({
    kind: '',
    opened_on: '',
    case_number: finding.case_number ?? '',
    claims_deadline: finding.claims_deadline ?? '',
    completed_on: '',
  })
  const [rejecting, setRejecting] = useState(false)
  const [note, setNote] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const run = async (action, doneText) => {
    setBusy(true)
    setError(null)
    try {
      await action()
      onDone(doneText)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const confirm = (event) => {
    event.preventDefault()
    run(
      () => api.confirmRegistryFinding(finding.id, payloadOf(finding, values)),
      'Находка подтверждена: сведения о банкротстве внесены.',
    )
  }
  const reject = (event) => {
    event.preventDefault()
    run(
      () => api.rejectRegistryFinding(finding.id, note.trim()),
      'Находка отклонена, подача по счетам человека вернулась в работу.',
    )
  }

  const set = (key) => (event) => setValues({ ...values, [key]: event.target.value })
  const completed = finding.registry_state === 'completed'

  return (
    <section className="notice notice--wide notice--error">
      <div className="notice__what">Реестр банкротств нашёл должника: {finding.person_name}</div>
      <div>{finding.summary}</div>
      <div className="notice__do">Совпали: {finding.basis}.</div>
      <div className="notice__do">
        {finding.holds ? (
          <b>Подача по счёту остановлена: заявление не собирается, пока вы не разберёте находку.</b>
        ) : (
          <b>Совпадение спорное, подача не остановлена: сверьте человека сами.</b>
        )}
      </div>

      <form onSubmit={confirm}>
        <div className="row row--fields">
          {finding.kind ? null : (
            <div className="field field--m">
              <span className="field__label" id={`finding-kind-${finding.id}`}>
                Вид процедуры
              </span>
              <Select
                value={values.kind}
                onChange={(value) => setValues({ ...values, kind: value })}
                options={KINDS}
                empty="выберите вид"
                disabled={busy}
                labelledBy={`finding-kind-${finding.id}`}
              />
            </div>
          )}
          <Field label="Дата принятия заявления о банкротстве *" width="m">
            <input
              className="field__input"
              type="date"
              value={values.opened_on}
              required
              disabled={busy}
              onChange={set('opened_on')}
            />
          </Field>
          <Field label="Номер дела" width="m">
            <input
              className="field__input"
              type="text"
              value={values.case_number}
              disabled={busy}
              onChange={set('case_number')}
            />
          </Field>
          <Field label="Закрытие реестра требований" width="s">
            <input
              className="field__input"
              type="date"
              value={values.claims_deadline}
              disabled={busy}
              onChange={set('claims_deadline')}
            />
          </Field>
          {completed ? (
            <Field label="Дата завершения процедуры *" width="s">
              <input
                className="field__input"
                type="date"
                value={values.completed_on}
                required
                disabled={busy}
                onChange={set('completed_on')}
              />
            </Field>
          ) : null}
        </div>
        <div className="row row--tight">
          <button type="submit" className="button" disabled={busy}>
            Подтвердить находку
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={busy}
            onClick={() => setRejecting(!rejecting)}
          >
            Это не он: причина…
          </button>
        </div>
      </form>

      {rejecting ? (
        <form onSubmit={reject}>
          <div className="row row--fields">
            <Field label="Почему это другой человек" width="l">
              <input
                className="field__input"
                type="text"
                value={note}
                maxLength={1000}
                placeholder="Например: однофамилец, другое отчество"
                disabled={busy}
                onChange={(event) => setNote(event.target.value)}
              />
            </Field>
          </div>
          <button type="submit" className="button" disabled={busy || note.trim() === ''}>
            Отклонить находку
          </button>
        </form>
      ) : null}

      <Notice error={error} wide />
    </section>
  )
}
